function ymNum(key) {
  const m = String(key || '').match(/^(\d{4})-(\d{2})$/)
  if (!m) return 0
  return Number(`${m[1]}${m[2]}`)
}

function median(values) {
  if (!values.length) return 0
  const sorted = values.slice().sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2) return sorted[mid]
  return (sorted[mid - 1] + sorted[mid]) / 2
}

function quartile(sortedValues, q) {
  if (!sortedValues.length) return 0
  const pos = (sortedValues.length - 1) * q
  const base = Math.floor(pos)
  const rest = pos - base
  const next = sortedValues[base + 1]
  if (next === undefined) return sortedValues[base]
  return sortedValues[base] + rest * (next - sortedValues[base])
}

function resolvePeriodKey(row = {}) {
  const key = String(row.periodoKey || row.periodoISO || '').trim()
  if (/^\d{4}-\d{2}$/.test(key)) return key
  const slash = String(row.periodo || '').trim().match(/^(\d{2})\/(\d{4})$/)
  if (slash) return `${slash[2]}-${slash[1]}`
  return ''
}

function isValidPeriodKey(key) {
  const m = String(key || '').match(/^(\d{4})-(\d{2})$/)
  if (!m) return false
  const month = Number(m[2])
  return month >= 1 && month <= 12
}

function computeOutliersByCuit(rows, options = {}) {
  const byCuit = new Map()
  rows.forEach((row) => {
    const cuit = String(row.cuit || 'SIN_DATO').trim() || 'SIN_DATO'
    const monto = Number(row.monto || row.remImpSS || row.montoOriginal || 0)
    if (!(monto > 0) || !Number.isFinite(monto)) return
    const bucket = byCuit.get(cuit) || []
    bucket.push(monto)
    byCuit.set(cuit, bucket)
  })

  const minSample = Number(options.minOutlierSample || 6)
  const mildFactor = Number(options.iqrMildFactor || 1.5)
  const extremeFactor = Number(options.iqrExtremeFactor || 3)
  const blockingRatio = Number(options.outlierBlockingRatio || 0.2)

  const perCuit = []
  let mildTotal = 0
  let extremeTotal = 0
  let blockingByOutliers = false

  byCuit.forEach((values, cuit) => {
    const sorted = values.slice().sort((a, b) => a - b)
    const sampleSize = sorted.length
    const med = median(sorted)

    if (sampleSize < minSample) {
      perCuit.push({
        cuit,
        sampleSize,
        median: Number(med.toFixed(2)),
        mildOutliers: 0,
        extremeOutliers: 0,
        extremeRatio: 0,
        skippedBySample: true,
      })
      return
    }

    const q1 = quartile(sorted, 0.25)
    const q3 = quartile(sorted, 0.75)
    const iqr = Math.max(0, q3 - q1)
    const mildLow = q1 - mildFactor * iqr
    const mildHigh = q3 + mildFactor * iqr
    const extremeLow = q1 - extremeFactor * iqr
    const extremeHigh = q3 + extremeFactor * iqr

    let mild = 0
    let extreme = 0
    sorted.forEach((m) => {
      if (m < extremeLow || m > extremeHigh) {
        extreme += 1
      } else if (m < mildLow || m > mildHigh) {
        mild += 1
      }
    })

    const extremeRatio = sampleSize > 0 ? extreme / sampleSize : 0
    if (extremeRatio > blockingRatio) blockingByOutliers = true
    mildTotal += mild
    extremeTotal += extreme
    perCuit.push({
      cuit,
      sampleSize,
      median: Number(med.toFixed(2)),
      q1: Number(q1.toFixed(2)),
      q3: Number(q3.toFixed(2)),
      iqr: Number(iqr.toFixed(2)),
      mildOutliers: mild,
      extremeOutliers: extreme,
      extremeRatio: Number(extremeRatio.toFixed(3)),
      skippedBySample: false,
    })
  })

  return {
    perCuit,
    mildTotal,
    extremeTotal,
    blockingByOutliers,
    minSample,
    blockingRatio,
  }
}

export function evaluateLaborIntegrity(rows = [], options = {}) {
  const data = Array.isArray(rows) ? rows : []
  const byPeriodCuit = new Map()
  const monthSet = new Set()
  let duplicates = 0
  let overlaps = 0
  let corruptedPeriods = 0

  const grouped = new Map()
  data.forEach((r) => {
    const period = resolvePeriodKey(r)
    if (!period || !isValidPeriodKey(period)) {
      corruptedPeriods += 1
      return
    }

    monthSet.add(period)
    const cuit = String(r.cuit || 'SIN_DATO').trim() || 'SIN_DATO'
    const key = `${period}|${cuit}`
    if (byPeriodCuit.has(key)) duplicates += 1
    byPeriodCuit.set(key, true)

    const bucket = grouped.get(period) || []
    bucket.push(r)
    grouped.set(period, bucket)
  })

  grouped.forEach((items) => {
    const cuits = new Set(items.map((r) => String(r.cuit || 'SIN_DATO').trim() || 'SIN_DATO'))
    if (cuits.size > 1) overlaps += 1
  })

  const yms = Array.from(monthSet).map(ymNum).filter(Boolean).sort((a, b) => a - b)
  let gaps = 0
  for (let i = 1; i < yms.length; i += 1) {
    const prevY = Math.floor(yms[i - 1] / 100)
    const prevM = yms[i - 1] % 100
    const currY = Math.floor(yms[i] / 100)
    const currM = yms[i] % 100
    const prevIdx = prevY * 12 + prevM
    const currIdx = currY * 12 + currM
    const delta = currIdx - prevIdx
    if (delta > 1) gaps += (delta - 1)
  }

  const outlierAnalysis = computeOutliersByCuit(data, options)
  const blockingIssues = []
  const warningIssues = []
  const reasonCodes = []

  if (corruptedPeriods > 0) {
    blockingIssues.push(`Fechas/periodos corruptos detectados: ${corruptedPeriods} filas invalidas.`)
    reasonCodes.push('periodo_corrupto')
  }
  if (duplicates > 0) {
    blockingIssues.push(`Duplicados detectados: ${duplicates} filas con clave periodo+CUIT repetida.`)
    reasonCodes.push('duplicado_periodo_cuit')
  }
  if (overlaps > 0) {
    warningIssues.push(`Superposicion detectada: ${overlaps} periodos con multiempleador.`)
    reasonCodes.push('simultaneidad_legitima_detectada')
  }
  if (gaps > 0) {
    warningIssues.push(`Huecos detectados: ${gaps} meses sin aportes en secuencia.`)
    reasonCodes.push('huecos_cronologicos')
  }

  if (outlierAnalysis.extremeTotal > 0 || outlierAnalysis.mildTotal > 0) {
    const msg = `Outliers detectados: ${outlierAnalysis.extremeTotal + outlierAnalysis.mildTotal} remuneraciones fuera de banda (analisis por CUIT).`
    if (outlierAnalysis.blockingByOutliers) {
      blockingIssues.push(msg)
      reasonCodes.push('outliers_extremos_por_cuit')
    } else {
      warningIssues.push(msg)
      reasonCodes.push('outliers_leves_por_cuit')
    }
  }

  const blocking = blockingIssues.length > 0
  const status = blocking ? 'red' : (warningIssues.length > 0 ? 'yellow' : 'green')

  return {
    blocking,
    status,
    issues: [...blockingIssues, ...warningIssues],
    blockingIssues,
    warningIssues,
    reasonCodes,
    stats: {
      totalRows: data.length,
      uniquePeriods: monthSet.size,
      gaps,
      duplicates,
      overlaps,
      corruptedPeriods,
      mildOutliers: outlierAnalysis.mildTotal,
      extremeOutliers: outlierAnalysis.extremeTotal,
      outlierBlockingByRatio: outlierAnalysis.blockingByOutliers,
      outlierBlockingRatioThreshold: outlierAnalysis.blockingRatio,
      outlierMinSample: outlierAnalysis.minSample,
      perCuitOutliers: outlierAnalysis.perCuit,
    },
  }
}
